import { DETECTION_TIERS, FAMILY_LABEL, type AgentSummary, type Signal, type SignalDefinition, type SignalFamily, type SignalStat, type SignalTierClass } from "@ti/core";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Activity, AlertOctagon, ChevronRight, Gauge, Radar, ScanSearch } from "lucide-react";
import { useMemo, useRef } from "react";
import { z } from "zod";
import { TimeAgo } from "@/components/domain/misc";
import { Delta } from "@/components/domain/score";
import { StatTile } from "@/components/domain/stat";
import { SeverityPill } from "@/components/domain/status";
import { ErrorPanel, QueryState } from "@/components/layout/page";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { DataTable } from "@/components/ui/data-table";
import { EmptyState, Skeleton } from "@/components/ui/misc";
import { catalogColumns, type ExplorerRow } from "@/features/signals/catalog-columns";
import { DETECTION_TIER_META, DetectionTierBadge } from "@/features/signals/detection-tier";
import { FAMILIES, SignalHeatmap } from "@/features/signals/heatmap";
import { q } from "@/lib/api";
import { Select } from "@/components/ui/input";
import { fmtInt, fmtPct } from "@/lib/format";
import { usePackName } from "@/lib/packs";
import { cn } from "@/lib/utils";

const searchSchema = z.object({
  family: z.enum(FAMILIES as [SignalFamily, ...SignalFamily[]]).optional().catch(undefined),
  /** Pack id, or "core" for signal types defined by the core. */
  pack: z.string().min(1).optional().catch(undefined),
  /** Signal tier: risk (curated, feeds ARS) or analytics (customer-defined). */
  tier: z.enum(["risk", "analytics"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/signals/")({
  validateSearch: searchSchema,
  component: SignalsPage,
});

function median(xs: number[]) {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m]! : (s[m - 1]! + s[m]!) / 2;
}

function SignalsPage() {
  const { family, pack, tier } = Route.useSearch();
  const navigate = Route.useNavigate();
  const stats = useQuery(q.signalStats());
  const heat = useQuery(q.signalHeatmap());
  const agents = useQuery(q.agents());
  const catalogRef = useRef<HTMLDivElement>(null);

  const setFamily = (f: SignalFamily | undefined, scroll = false) => {
    void navigate({ search: (prev) => ({ ...prev, family: f }), replace: true, resetScroll: false });
    if (scroll) requestAnimationFrame(() => catalogRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };

  return (
    <>
      <Kpis stats={stats} />

      <Card className="mt-3">
        <CardHeader title="Where signals fire" description="Signal count by agent × family, last 7 days" />
        <CardBody className="pt-3">
          <QueryState query={heat} loading={<Skeleton className="h-[290px] w-full" />}>
            {(cells) =>
              cells.length ? (
                <SignalHeatmap cells={cells} agents={agents.data} activeFamily={family} onSelectFamily={(f) => setFamily(f === family ? undefined : f, true)} />
              ) : (
                <EmptyState icon={<Radar />} title="No signals yet" description="Once traces flow in, detector hits are grouped here by agent and family." />
              )
            }
          </QueryState>
        </CardBody>
      </Card>

      <div ref={catalogRef} className="scroll-mt-4">
        <Catalog
          stats={stats}
          agents={agents.data}
          family={family}
          onFamily={(f) => setFamily(f)}
          pack={pack}
          onPack={(p) => void navigate({ search: (prev) => ({ ...prev, pack: p }), replace: true, resetScroll: false })}
          tier={tier}
          onTier={(t) => void navigate({ search: (prev) => ({ ...prev, tier: t }), replace: true, resetScroll: false })}
        />
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_360px]">
        <RecentSignals agents={agents.data} />
        <TiersCard stats={stats.data} />
      </div>
    </>
  );
}

function Kpis({ stats }: { stats: UseQueryResult<SignalStat[]> }) {
  const k = useMemo(() => {
    const s = stats.data;
    if (!s) return null;
    const total = s.reduce((a, x) => a + x.count7d, 0);
    const severe = s.filter((x) => x.severity === "critical" || x.severity === "high").reduce((a, x) => a + x.count7d, 0);
    const critical = s.filter((x) => x.severity === "critical").reduce((a, x) => a + x.count7d, 0);
    const prev = s.reduce((a, x) => a + x.trend.slice(0, 7).reduce((b, y) => b + y, 0), 0);
    const cur = s.reduce((a, x) => a + x.trend.slice(7).reduce((b, y) => b + y, 0), 0);
    const delta = prev ? Math.round(((cur - prev) / prev) * 100) : 0;
    const firing = s.filter((x) => x.count7d > 0).length;
    const precisions = s.map((x) => x.precision);
    return { total, severe, critical, delta, firing, detectors: s.length, median: median(precisions), min: Math.min(...precisions) };
  }, [stats.data]);

  if (stats.error) return <ErrorPanel error={stats.error} reset={() => void stats.refetch()} />;
  if (!k)
    return (
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-[104px]" />
        ))}
      </div>
    );

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatTile label="Signals · 7d" icon={<Activity />} value={fmtInt(k.total)} sub={<Delta value={k.delta} suffix="% vs prior 7d" invert />} />
      <StatTile
        label="Critical + high"
        icon={<AlertOctagon />}
        value={fmtInt(k.severe)}
        sub={
          <>
            {fmtInt(k.critical)} critical · {k.total ? fmtPct(k.severe / k.total) : "0%"} of all signals
          </>
        }
      />
      <StatTile label="Detectors active" icon={<ScanSearch />} value={fmtInt(k.detectors)} sub={`${k.firing} fired in the last 7 days`} />
      <StatTile label="Median precision" icon={<Gauge />} value={fmtPct(k.median, 1)} sub={`Lowest ${fmtPct(k.min)} · calibrated per detector`} />
    </div>
  );
}

const TIER_OPTIONS: Array<{ value: SignalTierClass | undefined; label: string }> = [
  { value: undefined, label: "All tiers" },
  { value: "risk", label: "Risk" },
  { value: "analytics", label: "Analytics" },
];

/** Analytics definitions with a hit history (running or paused) appear in the catalog next to the risk detectors. */
const LISTED: ReadonlySet<SignalDefinition["status"]> = new Set(["shadow", "active", "paused"]);

function toRows(stats: SignalStat[], defs: SignalDefinition[] | undefined, agents: AgentSummary[] | undefined): ExplorerRow[] {
  const byType = new Map((defs ?? []).map((d) => [d.type, d]));
  const names = new Map(agents?.map((a) => [a.id, a.name]));
  const risk: ExplorerRow[] = stats.map((s) => {
    const d = byType.get(s.type);
    return { ...s, tierClass: "risk", method: d?.method ?? null, definitionId: d?.id ?? null, labeled: d?.labeled ?? null, status: d?.status ?? null };
  });
  const analytics: ExplorerRow[] = (defs ?? [])
    .filter((d) => d.tierClass === "analytics" && LISTED.has(d.status))
    .map((d) => {
      const half = Math.floor(d.hitTrend.length / 2);
      const prev = d.hitTrend.slice(0, half).reduce((a, b) => a + b, 0);
      const cur = d.hitTrend.slice(half).reduce((a, b) => a + b, 0);
      return {
        type: d.type,
        family: d.family,
        severity: d.severity,
        tier: d.detectionTier,
        count7d: d.hits7d,
        deltaPct: prev ? Math.round(((cur - prev) / prev) * 100) : 0,
        trend: d.hitTrend,
        agents: d.agentIds ? d.agentIds.map((id) => names.get(id) ?? id) : (agents?.map((a) => a.name) ?? []),
        precision: d.precision,
        pack: d.pack,
        tierClass: "analytics",
        method: d.method,
        definitionId: d.id,
        labeled: d.labeled,
        status: d.status,
      };
    });
  return [...risk, ...analytics];
}

function Catalog({
  stats,
  agents,
  family,
  onFamily,
  pack,
  onPack,
  tier,
  onTier,
}: {
  stats: UseQueryResult<SignalStat[]>;
  agents: AgentSummary[] | undefined;
  family?: SignalFamily;
  onFamily: (f: SignalFamily | undefined) => void;
  pack?: string;
  onPack: (p: string | undefined) => void;
  tier?: SignalTierClass;
  onTier: (t: SignalTierClass | undefined) => void;
}) {
  const navigate = Route.useNavigate();
  const packName = usePackName();
  const defs = useQuery(q.signalDefinitions());
  const columns = useMemo(() => catalogColumns(packName), [packName]);
  const all = useMemo(() => (stats.data ? toRows(stats.data, defs.data, agents) : undefined), [stats.data, defs.data, agents]);
  const packKey = (s: ExplorerRow) => s.pack ?? "core";
  const tierCounts = useMemo(() => {
    const m = new Map<SignalTierClass, number>();
    for (const s of all ?? []) m.set(s.tierClass, (m.get(s.tierClass) ?? 0) + 1);
    return m;
  }, [all]);
  const byTier = useMemo(() => (tier ? all?.filter((s) => s.tierClass === tier) : all), [all, tier]);
  const byPack = useMemo(() => (pack ? byTier?.filter((s) => packKey(s) === pack) : byTier), [byTier, pack]);
  const counts = useMemo(() => {
    const m = new Map<SignalFamily, number>();
    for (const s of byPack ?? []) m.set(s.family, (m.get(s.family) ?? 0) + 1);
    return m;
  }, [byPack]);
  const packOptions = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of byTier ?? []) m.set(packKey(s), (m.get(packKey(s)) ?? 0) + 1);
    return [...m].sort(([a], [b]) => (a === "core" ? -1 : b === "core" ? 1 : packName(a).localeCompare(packName(b))));
  }, [byTier, packName]);
  const rows = useMemo(() => (family ? byPack?.filter((s) => s.family === family) : byPack), [byPack, family]);

  const chip = (active: boolean) =>
    cn(
      "inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-full border px-2.5 text-xs font-medium transition-colors",
      active ? "border-accent/50 bg-accent-soft text-accent" : "border-border-strong text-fg-2 hover:bg-surface-2 hover:text-fg",
    );

  return (
    <Card className="mt-3">
      <CardHeader
        title="Signal catalog"
        description={all ? `${rows?.length ?? 0} of ${all.length} signal types · risk rows open matching traces, analytics rows open the definition` : "Loading…"}
        actions={
          <>
            <div role="radiogroup" aria-label="Signal tier" className="flex items-center rounded-md bg-surface-2 p-0.5">
              {TIER_OPTIONS.map((o) => {
                const active = tier === o.value;
                const n = o.value ? (tierCounts.get(o.value) ?? 0) : (all?.length ?? 0);
                return (
                  <button
                    key={o.label}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => onTier(o.value)}
                    className={cn("inline-flex h-7 cursor-pointer items-center gap-1 rounded px-2.5 text-xs font-medium", active ? "bg-surface text-fg shadow-sm" : "text-fg-3 hover:text-fg")}
                  >
                    {o.label} <span className="tabular text-fg-3">{all ? n : ""}</span>
                  </button>
                );
              })}
            </div>
            <Select aria-label="Pack" value={pack ?? ""} onChange={(e) => onPack(e.target.value || undefined)} className="w-48">
              <option value="">All packs</option>
              {pack && !packOptions.some(([k]) => k === pack) ? <option value={pack}>{packName(pack === "core" ? null : pack)}</option> : null}
              {packOptions.map(([k, n]) => (
                <option key={k} value={k}>
                  {packName(k === "core" ? null : k)} · {n}
                </option>
              ))}
            </Select>
          </>
        }
      />
      <div role="group" aria-label="Filter by family" className="flex flex-wrap gap-1.5 border-b px-4 py-2.5">
        <button type="button" aria-pressed={!family} className={chip(!family)} onClick={() => onFamily(undefined)}>
          All <span className="text-fg-3 tabular">{byPack?.length ?? ""}</span>
        </button>
        {FAMILIES.filter((f) => counts.get(f)).map((f) => (
          <button key={f} type="button" aria-pressed={family === f} className={chip(family === f)} onClick={() => onFamily(family === f ? undefined : f)}>
            {FAMILY_LABEL[f]} <span className="text-fg-3 tabular">{counts.get(f)}</span>
          </button>
        ))}
      </div>
      {stats.error ? (
        <ErrorPanel error={stats.error} reset={() => void stats.refetch()} className="m-4" />
      ) : (
        <DataTable
          data={rows}
          loading={stats.isPending}
          columns={columns}
          dense
          stickyHeader={false}
          getRowId={(s) => `${s.tierClass}:${s.type}`}
          onRowClick={(s) =>
            s.tierClass === "analytics" && s.definitionId
              ? void navigate({ to: "/signals/studio/$definitionId", params: { definitionId: s.definitionId } })
              : void navigate({ to: "/traces", search: { q: `signal:${s.type}` } })
          }
          empty={{ icon: <Radar />, title: "No signal types match", description: tier === "analytics" ? "No analytics signals are running yet. Accept a proposal or write one in the Studio." : "Pick another tier, family or pack, or clear the filters." }}
        />
      )}
    </Card>
  );
}

function RecentSignals({ agents }: { agents: AgentSummary[] | undefined }) {
  const recent = useQuery(q.signals({ limit: 30 }));
  const names = useMemo(() => new Map(agents?.map((a) => [a.id, a.name])), [agents]);
  return (
    <Card className="flex min-w-0 flex-col">
      <CardHeader title="Recent signals" description="Latest 30 detector hits across all agents" />
      <QueryState query={recent} loading={<Skeleton className="m-4 h-72" />}>
        {(list) =>
          list.length ? (
            <ul className="scrollbar-thin max-h-[520px] divide-y overflow-y-auto">
              {list.map((s) => (
                <li key={s.id}>
                  <RecentRow s={s} agentName={names.get(s.agentId) ?? s.agentId} />
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState icon={<Radar />} title="No recent signals" description="Detector hits appear here as traces are scored." />
          )
        }
      </QueryState>
    </Card>
  );
}

function RecentRow({ s, agentName }: { s: Signal; agentName: string }) {
  return (
    <Link
      to="/traces/$traceId"
      params={{ traceId: s.traceId }}
      search={{ span: s.spanId }}
      className="group grid grid-cols-[92px_minmax(0,1fr)_auto] items-start gap-3 px-4 py-2.5 hover:bg-surface-2 focus-visible:bg-surface-2 focus-visible:outline-none"
    >
      <span className="pt-px">
        <SeverityPill severity={s.severity} />
      </span>
      <span className="min-w-0">
        <span className="flex min-w-0 items-center gap-2">
          <span className="truncate font-mono text-[12px] text-fg">{s.type}</span>
          <span className="shrink-0 font-mono text-[11.5px] text-fg-3">{agentName}</span>
          {s.issueId ? <span className="shrink-0 rounded border px-1 font-mono text-[10.5px] text-fg-3">{s.issueId}</span> : null}
        </span>
        <span className="mt-0.5 block truncate text-xs text-fg-2" title={s.explanation}>
          {s.explanation}
        </span>
      </span>
      <span className="flex items-center gap-1 pt-px text-xs text-fg-3">
        <TimeAgo iso={s.ts} className="text-fg-3" />
        <ChevronRight className="size-3.5 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" aria-hidden />
      </span>
    </Link>
  );
}

function TiersCard({ stats }: { stats: SignalStat[] | undefined }) {
  const byTier = useMemo(() => {
    const m = new Map<string, { detectors: number; count: number }>();
    for (const s of stats ?? []) {
      const e = m.get(s.tier) ?? { detectors: 0, count: 0 };
      e.detectors++;
      e.count += s.count7d;
      m.set(s.tier, e);
    }
    return m;
  }, [stats]);

  return (
    <Card>
      <CardHeader title="Detection tiers" description="Cheap checks run everywhere; expensive judges only where they matter" />
      <ul className="divide-y">
        {DETECTION_TIERS.map((t) => {
          const m = DETECTION_TIER_META[t];
          const e = byTier.get(t);
          return (
            <li key={t} className="flex gap-3 px-4 py-3">
              <DetectionTierBadge tier={t} className="mt-0.5 h-6 w-6 justify-center text-[12px]" />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[13px] font-medium text-fg">{m.name}</span>
                  {e ? (
                    <span className="text-[11px] text-fg-3 tabular">
                      {e.detectors} detectors · {fmtInt(e.count)} hits
                    </span>
                  ) : null}
                </div>
                <p className="text-xs text-fg-2">Runs on {m.runsOn.toLowerCase()}</p>
                <p className="mt-0.5 text-xs text-fg-3">{m.detail}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
